import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { List, Typography, Spin, Pagination } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { storefrontApi, type NewsListItem } from '@/apis/storefrontApi';
import { formatTime } from '@/utils/commonUtils';
import styles from './index.module.less';

const { Title, Paragraph } = Typography;

const News: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [list, setList] = useState<NewsListItem[]>([]);
  const [total, setTotal] = useState(0);
  const pageSize = 10;

  useEffect(() => {
    setLoading(true);
    storefrontApi
      .getNewsList({ page, pageSize })
      .then((res) => {
        setList(res.list);
        setTotal(res.total);
      })
      .catch(() => {
        setList([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [page, pageSize, i18n.language]);

  return (
    <div className={styles.page}>
      <div className={styles.inner}>
        <Title level={2} className={styles.title}>
          {t('news.title')}
        </Title>
        <Paragraph className={styles.desc}>
          {t('news.description')}
        </Paragraph>

        {loading ? (
          <div className={styles.loading}>
            <Spin size="large" />
          </div>
        ) : (
          <>
            <List
              className={styles.list}
              itemLayout="horizontal"
              dataSource={list}
              renderItem={(item) => (
                <List.Item className={styles.listItem}>
                  <Link to={`/news/${item.id}`} className={styles.itemLink}>
                    {item.coverImage && (
                      <div className={styles.itemCover}>
                        <img src={item.coverImage} alt={item.title} />
                      </div>
                    )}
                    <div className={styles.itemMain}>
                      <Title level={5} className={styles.itemTitle}>
                        {item.title}
                      </Title>
                      {item.summary && (
                        <Paragraph
                          type="secondary"
                          className={styles.itemSummary}
                          ellipsis={{ rows: 2 }}
                        >
                          {item.summary}
                        </Paragraph>
                      )}
                      <span className={styles.itemDate}>
                        <CalendarOutlined /> {formatTime(item.publishedAt)}
                      </span>
                    </div>
                  </Link>
                </List.Item>
              )}
            />
            {total > pageSize && (
              <div className={styles.pagination}>
                <Pagination
                  current={page}
                  pageSize={pageSize}
                  total={total}
                  onChange={setPage}
                  showSizeChanger={false}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default News;
